import DictTracker from "../../../prop-trackers/dict-tracker.js";
import MuxSource from "../../../prop-tracker-utils/mux-source.js";
import PropTracker from "../../../prop-trackers/prop-tracker.js";
import PropChainTracker from "../../../prop-tracker-utils/prop-chain-tracker.js";
import DictTrackerObjGen from "../../../prop-tracker-utils/dict-tracker-obj-gen.js";
import { Combiner } from "../../../prop-tracker-utils/combiner.js";

import ShallowObjectMerger from "../../../utils/shallow-object-merger.js";

import EmptyObjectTrackerCreator from "./objects/empty-object-tracker-creator.js";
import MeshObjectTrackerCreator from "./objects/mesh-object-tracker-creator.js";

import ObjectMaterialMapper from "./objects/object-material-mapper.js";
import ObjectGeometryMapper from "./objects/object-geometry-mapper.js";

const EmptyTypes = ["Object3D", "Group", "Bone"];
const MeshTypes = ["Mesh", "SkinnedMesh"];
const Types = EmptyTypes.concat(MeshTypes);

const NonLoadedMeshType = "Object3D";

class ObjectLoading {
    constructor(evtCtx, shlWrapCtx, shlEvtCtx, objectLoaders) {
        const geoMuxSource = new MuxSource();
        this._geoMuxSource = geoMuxSource;

        {
            this._emptyObjDataTrackers = {};
            this._emptyTrackLoaders = {};

            for (const type of EmptyTypes) {
                const dataTracker = new PropChainTracker(evtCtx);
                const trackLoader = new EmptyObjectTrackerCreator(dataTracker, objectLoaders[type]);
                const objGen = new DictTrackerObjGen(shlWrapCtx, shlEvtCtx, trackLoader);

                this._emptyObjDataTrackers[type] = dataTracker;
                this._emptyTrackLoaders[type] = trackLoader;
            }
        }

        {
            this._meshObjDataTrackers = {};
            this._meshMatMappers = {};
            this._meshTrackLoaders = {};
            this._meshTrackers = {};

            for (const type of MeshTypes) {
                const emptyLoader = objectLoaders[NonLoadedMeshType];
                const meshLoader = objectLoaders[type];

                const dataTrackers = {
                    object: new PropChainTracker(evtCtx),
                    geometry: new PropChainTracker(evtCtx),
                    material: new PropChainTracker(evtCtx),
                };

                const geoMapper = new ObjectGeometryMapper(dataTrackers.geometry, geoMuxSource, evtCtx);
                const matMapper = new ObjectMaterialMapper(dataTrackers.material, evtCtx, shlWrapCtx, shlEvtCtx);
                const trackLoader = new MeshObjectTrackerCreator(dataTrackers.object, geoMapper, matMapper, evtCtx, shlEvtCtx, emptyLoader, meshLoader);
                const meshTrackers = { empty: new DictTracker(), mesh: new DictTracker() };

                this._meshObjDataTrackers[type] = dataTrackers;
                this._meshMatMappers[type] = matMapper;
                this._meshTrackLoaders[type] = trackLoader;
                this._meshTrackers[type] = meshTrackers;
            }
        }

        const mainEmptyMerger = new Combiner((key) => new ShallowObjectMerger());
        mainEmptyMerger.setSource(0, this._emptyTrackLoaders[NonLoadedMeshType]);

        for (const [i, type] of MeshTypes.entries()) {
            const mergeSource = this._meshTrackers[type].empty;
            mainEmptyMerger.setSource(i+1, mergeSource);
        }

        const mainEmptyGen = new DictTrackerObjGen(shlWrapCtx, shlEvtCtx, mainEmptyMerger);

        const objGenByType = {
            [NonLoadedMeshType]: mainEmptyGen,
        };

        for (const type of EmptyTypes) {
            if (type !== NonLoadedMeshType) {
                objGenByType[type] = new DictTrackerObjGen(shlWrapCtx, shlEvtCtx, this._emptyTrackLoaders[type]);
            }
        }

        for (const type of MeshTypes) {
            objGenByType[type] = new DictTrackerObjGen(shlWrapCtx, shlEvtCtx, this._meshTrackers[type].mesh);
        }

        const merger = new Combiner((key) => new ShallowObjectMerger());

        for (const [i, type] of Types.entries()) {
            const objGen = objGenByType[type];
            const mergeSource = new PropTracker();
            mergeSource.track(objGen.data, objGen.events);
            merger.setSource(i, mergeSource);
        }

        const mergeGen = new DictTrackerObjGen(shlWrapCtx, shlEvtCtx, merger);

        this._objects = Object.freeze({
            byType: Object.freeze(objGenByType),
            all: mergeGen,
        });
    }

    get objects() { return this._objects; }

    track(objectDataByType, objectGeoDataByType, objectMatDataByType, geometries, materials) {
        if (objectDataByType) {
            const structPath = ["value"];

            for (const type of EmptyTypes) {
                this._emptyObjDataTrackers[type].track(objectDataByType[type].data, structPath);
            }

            for (const type of MeshTypes) {
                const trackers = this._meshObjDataTrackers[type];

                trackers.object.track(objectDataByType[type].data, structPath);
                trackers.geometry.track(objectGeoDataByType[type].data, structPath);
                trackers.material.track(objectMatDataByType[type].data, structPath);
            }
        } else {
            for (const type of EmptyTypes) {
                this._emptyObjDataTrackers[type].track();
            }

            for (const type of MeshTypes) {
                const trackers = this._meshObjDataTrackers[type];

                trackers.object.track();
                trackers.geometry.track();
                trackers.material.track();
            }
        }

        if (geometries) {
            this._geoMuxSource.setSource(geometries.data, geometries.events);
        } else {
            this._geoMuxSource.setSource();
        }

        if (materials) {
            for (const type of MeshTypes) {
                this._meshMatMappers[type].trackMaterials(materials.data, materials.events);
            }
        } else {
            for (const type of MeshTypes) {
                this._meshMatMappers[type].trackMaterials();
            }
        }
    }

    update() {
        for (const type of MeshTypes) {
            this._meshTrackLoaders[type].updateIfNeeded(({ trackers }) => {
                for (const tracker of trackers) {
                    tracker.update(({ userdata: loadState, props }) => {
                        const meshTracker = this._meshTrackers[type][loadState];

                        for (const { key, newExists, newValue } of props.iterateModified()) {
                            if (newExists) {
                                meshTracker.setValue(key, newValue);
                            } else {
                                meshTracker.deleteValue(key);
                            }
                        }
                    });
                }
            });
        }

        for (const type of Types) {
            this._objects.byType[type].update();
        }

        this._objects.all.update();
    }
}

export default ObjectLoading;
